import type { FinalInvoiceStatus } from '@arhs-fleetback/types/entities/interventions';
import { v4 as uuidv4 } from 'uuid';

import { CHECKLIST_STATUS, CHECKLIST_DONE, TYPES } from 'constants/ChecklistConstants';
import { SALES_MESSAGE } from 'constants/ModuleConstants';

import * as appointmentTypes from 'modules/appointments/actionTypes';
import { FETCH_LINKED_AUDITS_SUCCESS } from 'modules/audits/actionTypes';
import * as chatTypes from 'modules/chat/actionTypes';
import { FETCH_FOLLOW_UPS_SUCCESS } from 'modules/followUp/actionTypes';
import type { Action, PageContent, PayloadAction } from 'modules/global/generic.types';
import { StepsCodeEnum } from 'modules/global/typing/enums/steps.enum';
import * as actionTypes from 'modules/interventions/actionTypes';
import { closeInterventionSocketMatcher, deleteInterventionSocketMatcher, normalizeFinalInvoice } from 'modules/interventions/reducers/utils';
import * as invoiceTypes from 'modules/invoices/actionTypes';
import * as leadsTypes from 'modules/leads/actionTypes';
import type { FetchLeadsSuccessAction } from 'modules/leads/types';
import { SEND_SELF_SERVICE_SUCCESS } from 'modules/selfServices/actionTypes';
import * as stepTypes from 'modules/steps/actionTypes';
import { formatStatus } from 'utils/global';
import { uniq } from 'utils/ObjectUtils';
import { createReducer, createEntityAdapter } from 'utils/ReduxUtils';
import type { EntitySlice } from 'utils/ReduxUtils';

import type { KanbanEnum } from '../types/interfaces/kanban';
import type { DetailedIntervention } from '../types/projections/detailed-intervention';
import type { KanbanIntervention } from '../types/projections/kanban-intervention';
import type { StoredIntervention } from '../types/stored-intervention';

const interventionAdapter = createEntityAdapter<StoredIntervention>({ selectId: ({ id }) => id as string });

/** The value stored per id in the interventions data reducer. */
export type InterventionDataEntity = EntitySlice<StoredIntervention>;

const deleteOne = (state: InterventionDataEntity, { payload: { interventionId } }: PayloadAction<{ interventionId: string }>) =>
  interventionAdapter.removeOne(state, interventionId);

const deleteFromKanbanWebSocket = (state: InterventionDataEntity, { payload: { entityId } }: PayloadAction<{ entityId: string }>) =>
  interventionAdapter.removeOne(state, entityId);

const afterFetchOne = (state: InterventionDataEntity, { payload }: PayloadAction<DetailedIntervention>) => {
  const { step: _step, assignedUser: _assignedUser, assignedUserGroup: _assignedUserGroup, ...intervention } = payload;
  const isDirectMessage = intervention.module === SALES_MESSAGE;

  /* To be compliant with the old intervention, we create a "fake" group checklist */
  const hasUngroupedChecklist = intervention.checklists?.some(({ checklistGroupId, type }) => !checklistGroupId && type !== CHECKLIST_DONE);
  if (isDirectMessage) {
    if (!intervention.checklistGroups || hasUngroupedChecklist) {
      const defaultGroup = { id: uuidv4(), name: null, status: CHECKLIST_STATUS.PENDING, isCompatibility: true };
      intervention.checklists = intervention.checklists?.map(checklist => ({
        ...checklist,
        checklistGroupId: defaultGroup.id
      }));

      // @ts-expect-error defaultGroup.name is null on the compatibility group, ChecklistGroup expects string
      intervention.checklistGroups = [defaultGroup, ...(intervention.checklistGroups ?? [])];
    }
  }

  return interventionAdapter.addOne(state, {
    ...intervention,
    ...normalizeFinalInvoice(intervention)
  } as Partial<StoredIntervention>);
};

const refreshInterventionDashboard = (
  state: InterventionDataEntity,
  { payload: { content: interventions } }: PayloadAction<PageContent<DetailedIntervention>>
) =>
  interventionAdapter.addMany(
    state,
    interventions.map(intervention => ({
      ...intervention,
      ...(intervention.step?.id ? { stepId: intervention.step?.id } : {}),
      ...normalizeFinalInvoice(intervention)
    }))
  );

const addPaginatedElement = (state: InterventionDataEntity, { payload: { content: interventions } }: PayloadAction<PageContent<DetailedIntervention>>) =>
  interventionAdapter.updateMany(
    state,
    interventions.map(intervention => ({
      ...intervention,
      ...(intervention.step?.id ? { stepId: intervention.step?.id } : {}),
      ...normalizeFinalInvoice(intervention)
    }))
  );

const afterUpdateOne = (state: InterventionDataEntity, { payload: intervention }: PayloadAction<DetailedIntervention>) => interventionAdapter.addOne(state, {
  ...intervention,
  checklists: !intervention.checklists ? [] : intervention.checklists,
  internalMessage: !intervention.internalMessage ? '' : intervention.internalMessage
});

const afterReadInterventionMessage = (
  state: InterventionDataEntity,
  { payload: intervention }: PayloadAction<DetailedIntervention>
) => {
  const prev = (state.entities[intervention.id!] ?? {}) as Partial<DetailedIntervention>;

  const toSave = {
    id: intervention.id,
    conversationContext: {
      ...(prev.conversationContext ?? {}),
      unreadCount: 0
    }
  };

  return interventionAdapter.updateOne(state, toSave);
};

const updateFromAppointments = (
  state: InterventionDataEntity,
  { payload: { content: appointments } }: PayloadAction<PageContent<{ intervention?: DetailedIntervention }>>
) => {
  const interventions = appointments.reduce<DetailedIntervention[]>((acc, { intervention }) => {
    if (intervention) {
      acc.push(intervention);
    }
    return acc;
  }, []);
  return interventionAdapter.updateMany(state, interventions);
};

const updateFromNestedEntity = (state: InterventionDataEntity, { payload: { intervention } }: PayloadAction<{ intervention: DetailedIntervention }>) =>
  interventionAdapter.updateOne(state, { ...intervention, medias: intervention?.medias ?? [] });

const updateFromFetchedLeads = (state: InterventionDataEntity, { payload: { content: leads } }: FetchLeadsSuccessAction) => {
  const interventions = leads.flatMap(({ intervention, directMessage }) => [
    intervention,
    ...(directMessage ? [directMessage] : [])
  ]);
  return interventionAdapter.updateMany(state, interventions);
};

const updateFromPageResultObject = (state: InterventionDataEntity, { payload: { content: interventions } }: PayloadAction<PageContent<DetailedIntervention>>) =>
  interventionAdapter.updateMany(state, interventions.map(intervention => ({
    ...intervention,
    ...normalizeFinalInvoice(intervention)
  })));

const updateFromKanban = (
  state: InterventionDataEntity,
  { payload, meta }: PayloadAction<Record<KanbanEnum, KanbanIntervention[]>>
) => {
  const { ids, isEndDateDirty } = (meta ?? {}) as { ids?: string[]; isEndDateDirty?: boolean };
  let interventions: Partial<StoredIntervention>[] = Object.values(payload).flat();

  if ((ids?.length ?? 0) > 0 && isEndDateDirty) {
    interventions = interventions.map(intervention => ({
      ...intervention,
      endDate: (isEndDateDirty ? intervention.endDate ?? null : intervention.endDate) as string | undefined
    }));
  }

  return interventionAdapter.updateMany(state, interventions);
};

type ImportsPayload = {
  id: string;
  interventionId?: string;
  checklists?: DetailedIntervention['checklists'];
  checklistGroups?: DetailedIntervention['checklistGroups'];
  finalInvoice?: DetailedIntervention['finalInvoice'];
  finalInvoiceStatus?: FinalInvoiceStatus;
};

const updateFromImports = (state: InterventionDataEntity, { payload }: PayloadAction<ImportsPayload>) => {
  const { interventionId = payload.id, checklists, checklistGroups, finalInvoice, finalInvoiceStatus } = payload;
  const updates = {
    id: interventionId,
    ...(checklists && { checklists }),
    ...(checklistGroups && { checklistGroups }),
    ...(finalInvoice && { finalInvoice }),
    ...(finalInvoiceStatus && { finalInvoiceStatus })
  };
  return interventionAdapter.updateOne(state, updates as Partial<StoredIntervention>);
};

const updateFromKanbanMove = (state: InterventionDataEntity, { payload }: PayloadAction<{ intervention: KanbanIntervention }>) =>
  interventionAdapter.updateOne(state, payload.intervention);

const updateFromParentEntity = (
  state: InterventionDataEntity,
  { payload }: PayloadAction<PageContent<{ intervention?: DetailedIntervention; partsIntervention?: DetailedIntervention }>>
) => {
  const entities = payload.content.map(({ intervention, partsIntervention }) => intervention ?? partsIntervention);
  return interventionAdapter.updateMany(state, entities as Partial<StoredIntervention>[]);
};

const closeFromKanbanWebSocket = (state: InterventionDataEntity, { payload }: PayloadAction<{ entityId: string }>) =>
  interventionAdapter.updateOne(state, { id: payload.entityId, status: 'CLOSED' });

const updateFollowing = (state: InterventionDataEntity, { payload: intervention }: PayloadAction<DetailedIntervention>) =>
  interventionAdapter.updateOne(state, { id: intervention.id, following: intervention.following });

const setFromLinkedInterventions = (state: InterventionDataEntity, { payload }: PayloadAction<{ sameReferenceInterventions: DetailedIntervention[] }>) => (
  payload.sameReferenceInterventions.reduce((acc, intervention) =>
    interventionAdapter.updateOne(acc, intervention), state));

const afterSendSelfServiceSuccess = (
  state: InterventionDataEntity,
  { payload, meta }: PayloadAction<{ intervention: DetailedIntervention; id: string; originContext: Record<string, unknown> }>
) => {
  if (meta?.source !== 'mobility-dashboard') {
    const { intervention, id: selfServiceId, originContext } = payload;
    const currentIntervention = state.entities[intervention.id!] as (DetailedIntervention & { finalInvoiceStatus?: FinalInvoiceStatus }) | undefined;

    if (!currentIntervention) {
      return state;
    }

    const updatedSelfServices = currentIntervention.selfServices?.map(selfService => ({
      ...selfService,
      originContext: {
        ...(selfService.originContext ?? {}),
        ...(selfService.id === selfServiceId ? originContext : {})
      }
    }));

    const changes = {
      ...intervention,
      finalInvoiceStatus: intervention.finalInvoice?.status ?? currentIntervention.finalInvoiceStatus,
      selfServices: updatedSelfServices
    };

    return interventionAdapter.updateOne(state, changes as Partial<StoredIntervention>);
  }
  return state;
};

type ConversationMessage = { partsInterventionId?: string; interventionId?: string; channel?: string };

const updateConversationContext = (
  state: InterventionDataEntity,
  action: Action<ConversationMessage & { message?: ConversationMessage }>
) => {
  const message = action.payload?.message ?? action.payload;
  const entityId = message?.partsInterventionId ?? message?.interventionId;

  if (entityId) {
    const entity = (state.entities[entityId] ?? {}) as Partial<DetailedIntervention>;
    const channels = uniq([...(entity.conversationContext?.channels ?? []), message?.channel]);

    return interventionAdapter.updateOne(state, {
      id: entityId,
      conversationContext: {
        ...(entity.conversationContext ?? {}),
        channels
      }
    } as Partial<StoredIntervention>);
  }
  return state;
};

const afterChangeInterventionStepSuccess = (state: InterventionDataEntity, { payload: intervention }: PayloadAction<DetailedIntervention>) => {
  const { id, serviceAdvisorUser, serviceAdvisorUserGroup, step, followUp } = intervention;
  return interventionAdapter.updateOne(state, {
    id,
    serviceAdvisorUser,
    serviceAdvisorUserGroup,
    ...(step?.code === StepsCodeEnum.FOLLOW_UP && { followUp }),
  });
};

type WorkOrderWork = {
  type: string;
  status: string;
  isImported?: boolean;
};

const afterFetchWorksOrderSuccess = (
  state: InterventionDataEntity,
  { payload }: PayloadAction<{ interventionId: string; workOrder: { workOrderWorks: WorkOrderWork[] } }>
) => {
  const { workOrder, interventionId } = payload;

  const workOrderWorks = workOrder.workOrderWorks.map(order => ({
    ...order,
    status: formatStatus(order.type, order.status)
  })).sort((workA, workB) => {
    if (workA.type === workB.type) {
      if (workA.isImported) {
        return -1;
      }
      return 0;
    }
    if (workA.type === TYPES.DONE) {
      return -1;
    }
    return 1;
  });

  return interventionAdapter.updateOne(state, { id: interventionId, workOrderWorks } as Partial<StoredIntervention>);
};

/** Extract intervention from payload after import success. */
const afterImportAuditsOrTscansSuccess = (state: InterventionDataEntity, { payload }: PayloadAction<{ intervention: DetailedIntervention }>) => {
  const { intervention } = payload;
  return interventionAdapter.updateOne(state, intervention);
};

export default createReducer(
  interventionAdapter.getInitialState(),
  {
    // Generic actions
    [actionTypes.SEND_INTERVENTION_SUCCESS]: interventionAdapter.updateOne,
    [actionTypes.POST_SEND_BY_SUCCESS]: interventionAdapter.updateOne,
    [actionTypes.CREATE_DEFAULT_STOCK_CHECKLIST_SUCCESS]: interventionAdapter.updateOne,
    [actionTypes.IMPORT_AUDITS_OR_TSCANS_SUCCESS]: afterImportAuditsOrTscansSuccess,
    [SEND_SELF_SERVICE_SUCCESS]: afterSendSelfServiceSuccess,
    [actionTypes.CREATE_INTERVENTION_SUCCESS]: interventionAdapter.addOne,
    [actionTypes.POST_DUPLICATE_INTERVENTION_SUCCESS]: interventionAdapter.addOne,
    [actionTypes.INTERVENTION_INVOICE_PATCH_STATUS_SUCCESS]: interventionAdapter.addOne,
    [stepTypes.CHANGE_INTERVENTION_STEP_SUCCESS]: afterChangeInterventionStepSuccess,
    [actionTypes.FETCH_WORKS_ORDER_SUCCESS]: afterFetchWorksOrderSuccess,
    // Prepared
    [actionTypes.FETCH_INTERVENTIONS_SUCCESS]: refreshInterventionDashboard,
    [actionTypes.FETCH_INTERVENTIONS_SELECT_SUCCESS]: addPaginatedElement,
    [actionTypes.DELETE_INTERVENTION_SUCCESS]: deleteOne,
    [actionTypes.FETCH_INTERVENTION_SUCCESS]: afterFetchOne,
    [actionTypes.UPDATE_INTERVENTION_SUCCESS]: afterUpdateOne,
    [actionTypes.UPDATE_INTERVENTION_STATUS_SUCCESS]: afterUpdateOne,
    [actionTypes.APPROVE_MANUALLY_SUCCESS]: afterUpdateOne,
    [actionTypes.READ_INTERVENTION_MESSAGES_SUCCESS]: afterReadInterventionMessage,
    // Appointments
    [appointmentTypes.PUT_SUCCESS]: updateFromNestedEntity,
    [appointmentTypes.FETCH_APPOINTMENT_SUCCESS]: updateFromNestedEntity,
    [appointmentTypes.FETCH_INTERVENTION_SUCCESS]: updateFromNestedEntity,
    [appointmentTypes.FETCH_APPOINTMENTS_SUCCESS]: updateFromAppointments,
    // Leads
    [leadsTypes.FETCH_SUCCESS]: updateFromFetchedLeads,
    [leadsTypes.CREATE_DM_SUCCESS]: updateFromNestedEntity,
    [actionTypes.INTERVENTIONS_FETCH_SUCCESS]: updateFromPageResultObject,
    // Kanban
    [actionTypes.FETCH_KANBAN_INTERVENTIONS_SUCCESS]: updateFromKanban,
    [actionTypes.MOVE_KANBAN_INTERVENTION_SUCCESS]: updateFromKanbanMove,
    // Mobility
    [actionTypes.FETCH_MOBILITY_INTERVENTIONS_SUCCESS]: updateFromPageResultObject,
    // Import Invoice
    [invoiceTypes.IMPORT_INVOICE_SUCCESS]: updateFromImports,
    [actionTypes.IMPORT_WORKS_INTO_INTERVENTION_SUCCESS]: updateFromImports,
    [actionTypes.IMPORT_TIRE_REPORT_SUCCESS]: updateFromImports,
    [actionTypes.IMPORT_CHECKLISTS_SUCCESS]: updateFromImports,
    // Follow
    [actionTypes.FOLLOW_FILE_SUCCESS]: updateFollowing,
    [actionTypes.UNFOLLOW_FILE_SUCCESS]: updateFollowing,

    // Follow-up
    [FETCH_FOLLOW_UPS_SUCCESS]: updateFromParentEntity,

    // Linked Interventions from audits
    [FETCH_LINKED_AUDITS_SUCCESS]: setFromLinkedInterventions,

    // Linked Interventions
    [actionTypes.FETCH_LINKED_INTERVENTIONS_SUCCESS]: addPaginatedElement,

    // Chat
    [chatTypes.RECEIVE_MESSAGE_FROM_SOCKET]: updateConversationContext,
    [chatTypes.POST_MESSAGE_SUCCESS]: updateConversationContext
  },
  [
    // Kanban (websocket)
    {
      matcher: closeInterventionSocketMatcher as (action: Action) => boolean,
      reducer: closeFromKanbanWebSocket
    },
    {
      matcher: deleteInterventionSocketMatcher as (action: Action) => boolean,
      reducer: deleteFromKanbanWebSocket
    }
  ]
);
